import React from 'react';

import { GradientProgressBar, IconFile } from '@trqk/components';
import { locale, removeSpecialCharacters } from '@trqk/utils';

import { UploadAudioError } from '../../../../../toastMessages/uploadAudio';
import { contentWidth, progressBarSpacer, rowHeight } from '../../../constants';
import { useTasks, useToast } from '../../../../../../hooks';

const height = rowHeight - 2 * progressBarSpacer;

interface AudioUploadProgressProps {
    task: UploadAudioTask;
}

/**
 * An upload progress row for an audio file upload.
 * @param {AudioUploadProgressProps} props
 */
export const AudioUploadProgressRow = ({
    task,
}: AudioUploadProgressProps): JSX.Element | null => {
    const { sendTaskEvent } = useTasks();
    const toast = useToast();
    const { taskId, canceled, done, error, input, output, progress, status } =
        task;
    const filename = removeSpecialCharacters(input?.filename ?? '');
    const assetUrl = output?.assetUrl ?? null;
    const trqkSongId = output?.trqkSongId ?? null;

    const errorDetails = {
        assetUrl,
        filename,
        error,
        trqkSongId,
    };

    const errorLabel = error
        ? locale.enUS.INGESTION.ERRORS.INGESTION_ERROR
        : null;

    if (done) {
        return null;
    }

    return (
        <div
            style={{
                marginTop: progressBarSpacer,
                marginBottom: progressBarSpacer,
            }}
        >
            <GradientProgressBar
                canceled={canceled}
                color="aqua"
                error={errorLabel}
                errorStyle="quiet"
                handleCancel={(): void => {
                    sendTaskEvent({
                        type: 'CANCEL',
                        value: {
                            taskId,
                        },
                    });
                }}
                handleExpandError={(): void => {
                    toast.add({
                        kind: 'warning-card',
                        RenderContent: UploadAudioError,
                        renderContentInput: {
                            errorDetails,
                        },
                        persist: true,
                    });
                }}
                height={height}
                Icon={IconFile}
                inProgressLabel={status ?? 'Uploading...'}
                label={filename ?? 'Audio file'}
                labelLines={2}
                progress={progress}
                width={contentWidth}
            />
        </div>
    );
};
